import { action, computed, makeObservable, observable, runInAction } from 'mobx';

import {
  AdminGetProfileQuery,
  AdminGetUserOrganizationQuery,
  AdminListAgenciesQuery,
  AgencyAdminOrderByOption,
  JobSpeciality,
  Order,
} from '@clipr/lib';

import { Store } from '../../store/store';
import { StoreNode } from '../../store';

export type AdminAgencySummary = AdminListAgenciesQuery['adminListAgencies']['edges'][number]['node'];
export type AdminLookedUpUser = NonNullable<AdminGetProfileQuery['adminGetProfile']>;
export type AdminLookedUpOrg = NonNullable<AdminGetUserOrganizationQuery['adminGetUserOrganization']>;

// Match backend AGENCY_AGGREGATION_PAGE_SIZE so the frontend doesn't silently
// drop agencies 101-200 that the ES composite agg returned.
const PAGE_SIZE = 200;

export class AdminAgenciesPageState extends StoreNode {

  constructor(store: Store) {
    super(store);
    makeObservable(this);
  }

  @observable agencies: AdminAgencySummary[] = [];
  @observable isLoading = false;
  @observable totalCount: number | null = null;
  @observable error: string | null = null;
  @observable search = '';
  @observable sortField: AgencyAdminOrderByOption = AgencyAdminOrderByOption.AgencyName;
  @observable sortOrder: Order = Order.Asc;
  // True when the backend reports hasNextPage = aggregation hit its cap. UI
  // surfaces a warning so the operator knows the list may be truncated.
  @observable resultsTruncated = false;

  /**
   * User lookup, by email or username. The agency workspace can only reach a user who is on some
   * agency's roster, and it opens at all only for an org that has an AgencyTemplate -- most do not.
   * The users this exists for are exactly the ones that gate excludes: uploading to My Uploads, in
   * an org with no template, or in no organization at all.
   */
  @observable lookupInput = '';
  @observable isLookingUp = false;
  @observable lookupError: string | null = null;
  /** The RAW stored row: an admin has to tell an explicit choice from an inherited one. */
  @observable user: AdminLookedUpUser | null = null;
  @observable org: AdminLookedUpOrg | null = null;
  /** Distinguishes a failed organization read from a confirmed user with no organization. */
  @observable orgUnavailable = false;
  @observable userDraft: JobSpeciality | null = null;
  @observable orgDraft: JobSpeciality | null = null;
  @observable isSavingUser = false;
  @observable isSavingOrg = false;
  @observable saveError: string | null = null;
  @observable saveMessage: string | null = null;
  // Bumped per lookup and per clear, so a response for a user the admin has moved off cannot land
  // on the card of the one they are looking at now -- this panel exists to be believed.
  private lookupSeq = 0;

  @computed
  get hasResults(): boolean {
    return this.agencies.length > 0;
  }

  /** What resolveDefaultJobSpeciality would return today: admin override, then the user, then org. */
  @computed
  get effectiveSpeciality(): JobSpeciality | null {
    return this.user?.adminDefaultJobSpeciality
      ?? this.user?.defaultJobSpeciality
      ?? this.org?.defaultJobSpeciality
      ?? null;
  }

  @action
  setLookupInput(value: string) {
    this.lookupInput = value;
  }

  @action
  clearLookup() {
    this.retireLookupState(true);
  }

  /** Retires every async owner of the current card so the next card always starts usable. */
  @action
  private retireLookupState(clearInput = false) {
    this.lookupSeq++;
    this.user = null;
    this.org = null;
    this.orgUnavailable = false;
    this.userDraft = null;
    this.orgDraft = null;
    this.isLookingUp = false;
    this.isSavingUser = false;
    this.isSavingOrg = false;
    if (clearInput) {
      this.lookupInput = '';
    }
    this.lookupError = null;
    this.saveError = null;
    this.saveMessage = null;
  }

  /**
   * An '@' is the only thing that separates the two identifiers -- usernames cannot contain one, and
   * the backend takes them on separate arguments because the email path is provider-scoped while the
   * username path reads the USERNAME# uniqueness row and so works on any identity provider.
   */
  @action
  async lookupUser() {
    const trimmed = this.lookupInput.trim();
    if (!trimmed) return;

    // A new search owns a new card. Retire reads and writes from the previous one before allowing
    // either response to update the replacement.
    this.retireLookupState();
    const seq = this.lookupSeq;
    this.isLookingUp = true;

    const [res, err] = await this.store.apiService.adminGetProfile(
      trimmed.includes('@') ? { email: trimmed } : { username: trimmed },
    );

    if (err || !res?.adminGetProfile) {
      runInAction(() => {
        if (seq !== this.lookupSeq) return;
        this.isLookingUp = false;
        this.lookupError = err ? err.message : `No CLIPr user found for ${trimmed}`;
      });
      return;
    }

    if (seq !== this.lookupSeq) return;

    const user = res.adminGetProfile;
    // The organization the user's defaults actually flow through, not one inferred from a team.
    const [orgRes, orgErr] = await this.store.apiService.adminGetUserOrganization({ userId: user.id });

    runInAction(() => {
      if (seq !== this.lookupSeq) return;

      this.isLookingUp = false;
      this.user = user;
      this.userDraft = user.adminDefaultJobSpeciality ?? null;
      this.org = orgErr ? null : (orgRes?.adminGetUserOrganization ?? null);
      this.orgUnavailable = Boolean(orgErr);
      this.orgDraft = this.org?.defaultJobSpeciality ?? null;
      // A failed org read is not a failed lookup: the per-user override still applies without it.
      this.lookupError = orgErr ? orgErr.message : null;
    });
  }

  @action
  setUserDraft(speciality: JobSpeciality | null) {
    this.userDraft = speciality;
  }

  @action
  setOrgDraft(speciality: JobSpeciality | null) {
    this.orgDraft = speciality;
  }

  @action
  async saveUserOverride() {
    const id = this.user?.id;
    const seq = this.lookupSeq;
    if (!id) return;

    this.isSavingUser = true;
    this.saveError = null;
    this.saveMessage = null;

    // The generated variables type has no null: an omitted argument is what clears the override,
    // and the resolver writes an explicit null for it.
    const [res, err] = await this.store.apiService.adminSetDefaultJobSpeciality({
      id,
      speciality: this.userDraft ?? undefined,
    });

    runInAction(() => {
      // The write already went to the captured id. What must not happen is folding its result into
      // whatever card is on screen now -- that would show one user's saved override under another's
      // name, in the one panel whose job is to be trusted.
      if (seq !== this.lookupSeq) return;

      this.isSavingUser = false;
      if (err) {
        this.saveError = err.message;
        return;
      }
      const saved = res?.adminSetDefaultJobSpeciality?.adminDefaultJobSpeciality ?? null;
      if (this.user) {
        this.user = { ...this.user, adminDefaultJobSpeciality: saved ?? undefined };
      }
      this.userDraft = saved;
      this.saveMessage = saved ? 'Override saved.' : 'Override cleared.';
    });
  }

  @action
  async saveOrgDefault() {
    const orgId = this.org?.id;
    const seq = this.lookupSeq;
    if (!orgId) return;

    this.isSavingOrg = true;
    this.saveError = null;
    this.saveMessage = null;

    const [res, err] = await this.store.apiService.adminSetOrgDefaultJobSpeciality({
      orgId,
      speciality: this.orgDraft ?? undefined,
    });

    runInAction(() => {
      if (seq !== this.lookupSeq) return;

      this.isSavingOrg = false;
      if (err) {
        this.saveError = err.message;
        return;
      }
      const saved = res?.adminSetOrgDefaultJobSpeciality?.defaultJobSpeciality ?? null;
      if (this.org) {
        this.org = { ...this.org, defaultJobSpeciality: saved ?? undefined };
      }
      this.orgDraft = saved;
      this.saveMessage = saved ? 'Organization default saved.' : 'Organization default cleared.';
    });
  }

  @action
  async pageMounted() {
    await this.fetch();
  }

  @action
  pageUnmounted() {
    this.error = null;
    this.retireLookupState(true);
  }

  @action
  setSearch(value: string) {
    this.search = value;
  }

  @action
  setSort(field: AgencyAdminOrderByOption) {
    if (this.sortField === field) {
      this.sortOrder = this.sortOrder === Order.Asc ? Order.Desc : Order.Asc;
    } else {
      this.sortField = field;
      this.sortOrder = Order.Asc;
    }
    void this.fetch();
  }

  @action
  async applySearch() {
    await this.fetch();
  }

  @action
  async fetch() {
    this.isLoading = true;
    this.error = null;

    const trimmed = this.search.trim();
    const [listRes, listErr] = await this.store.apiService.adminListAgencies({
      first: PAGE_SIZE,
      filter: trimmed ? { _search: trimmed } : undefined,
      sort: { field: this.sortField, order: this.sortOrder },
    });
    // Sponsor-only signups never appear in the team-index aggregation; fetch
    // them separately and fold them in.
    const [zeroLibRes, zeroLibErr] = await this.store.apiService.adminListAgenciesWithoutLibraries();

    runInAction(() => {
      this.isLoading = false;
      if (listErr) {
        this.error = listErr.message;
        return;
      }
      const conn = listRes?.adminListAgencies;
      const withLibs: AdminAgencySummary[] = (conn?.edges ?? []).map((edge) => edge.node);

      const withoutLibs: AdminAgencySummary[] = !zeroLibErr
        ? (zeroLibRes?.adminListAgenciesWithoutLibraries ?? [])
        : [];

      // adminListAgencies wins on overlap (it has the richer aggregate fields).
      const seen = new Set(withLibs.map((a) => a.orgId));
      const filteredZeroLib = trimmed
        ? withoutLibs.filter((a) => {
          const q = trimmed.toLowerCase();
          return a.agencyName.toLowerCase().includes(q)
            || a.agencyDomain.toLowerCase().includes(q)
            || (a.sponsorEmail ?? '').toLowerCase().includes(q);
        })
        : withoutLibs;
      const merged = [...withLibs, ...filteredZeroLib.filter((a) => !seen.has(a.orgId))];

      this.agencies = merged;
      this.totalCount = merged.length;
      this.resultsTruncated = !!conn?.pageInfo.hasNextPage;
    });
  }
}
